import React from 'react'
import { Link } from 'react-router-dom'

function PrivacyPolicy() {
  return (
    <main className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-4" style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                letterSpacing: '-0.02em'
              }}>
                Privacy Policy
              </h1>
              
              <p className="text-muted mb-4" style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '16px',
                lineHeight: '24px'
              }}>
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <div style={{
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                fontSize: '16px',
                lineHeight: '28px',
                color: '#181a25'
              }}>
                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    1. Introduction
                  </h2>
                  <p className="mb-3">
                    Welcome to JobsReadyAI ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience on our website and in using our products and services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our AI-powered job application assistant.
                  </p>
                  <p>
                    By using JobsReadyAI, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    2. Information We Collect
                  </h2>
                  
                  <h3 className="h5 fw-semibold mb-2 mt-4">2.1 Account and Profile Information</h3>
                  <p className="mb-3">
                    When you create an account or use our services, we collect and store:
                  </p>
                  <ul className="mb-3">
                    <li><strong>Account Information:</strong> Your name, email address, and unique user identifier (provided by Auth0 authentication service)</li>
                    <li><strong>Profile Information:</strong> Your "About Me" description, contact information, work experiences, and educational background</li>
                    <li><strong>Credits and Usage:</strong> Your credit balance and usage history for AI-powered features</li>
                    <li><strong>Account Activity:</strong> Timestamps of account creation and last update</li>
                  </ul>

                  <h3 className="h5 fw-semibold mb-2 mt-4">2.2 Job Application Data</h3>
                  <p className="mb-3">
                    When you use our job application features, we collect and store:
                  </p>
                  <ul className="mb-3">
                    <li><strong>Job Postings:</strong> Job titles, company names, locations, full job descriptions, job posting URLs, and AI-generated summaries</li>
                    <li><strong>Application Status:</strong> The current state of each job application (e.g., imported, analyzing, resume created, applied, offer received, etc.)</li>
                    <li><strong>Status History:</strong> A history of state changes for each job application with timestamps</li>
                    <li><strong>Job Activities:</strong> Events related to your job applications, including when jobs are imported, analyzed, or updated (stored for 30 days)</li>
                  </ul>

                  <h3 className="h5 fw-semibold mb-2 mt-4">2.3 Usage and Activity Data</h3>
                  <p className="mb-3">
                    We automatically collect certain information about how you use our service:
                  </p>
                  <ul className="mb-3">
                    <li><strong>View History:</strong> Records of when you view interview preparation plans and other content (tracked by date)</li>
                    <li><strong>Feature Usage:</strong> Information about which features you use and how often</li>
                    <li><strong>Progress Tracking:</strong> Data about completed questions and other progress indicators</li>
                  </ul>

                  <h3 className="h5 fw-semibold mb-2 mt-4">2.4 Automatically Collected Technical Information</h3>
                  <p className="mb-3">
                    When you access our website, we automatically collect certain technical information, including:
                  </p>
                  <ul className="mb-3">
                    <li>IP address and approximate location data</li>
                    <li>Browser type and version</li>
                    <li>Device information</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>

                  <h3 className="h5 fw-semibold mb-2 mt-4">2.5 Information from Third-Party Services</h3>
                  <p className="mb-3">
                    We use Auth0 for authentication, which provides us with:
                  </p>
                  <ul className="mb-3">
                    <li>Your authentication credentials and login information</li>
                    <li>JWT tokens for secure API access</li>
                    <li>Basic profile information from your authentication provider</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    3. How We Use Your Information
                  </h2>
                  <p className="mb-3">We use the information we collect to:</p>
                  <ul className="mb-3">
                    <li><strong>Service Delivery:</strong> Provide, maintain, and improve our AI-powered job application assistant services</li>
                    <li><strong>Job Management:</strong> Store and organize your job postings, track application statuses, and maintain your job application history</li>
                    <li><strong>AI Processing:</strong> Generate summaries of job descriptions using AI services (OpenAI) to help you better understand job requirements</li>
                    <li><strong>Profile Management:</strong> Store and manage your professional profile information, including work experiences and education</li>
                    <li><strong>Credit System:</strong> Track and manage your credit balance for using premium AI features</li>
                    <li><strong>Activity Tracking:</strong> Record job-related activities and view history to provide you with insights into your job search progress</li>
                    <li><strong>Authentication:</strong> Verify your identity and secure access to your account through Auth0</li>
                    <li><strong>Communication:</strong> Send you important updates about your account, service changes, and respond to your support inquiries</li>
                    <li><strong>Marketing:</strong> Send you marketing communications about our services (only with your explicit consent)</li>
                    <li><strong>Analytics:</strong> Analyze usage patterns to improve our services and user experience</li>
                    <li><strong>Security:</strong> Detect, prevent, and address technical issues, security threats, and fraudulent activity</li>
                    <li><strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and legal processes</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    4. How We Share Your Information
                  </h2>
                  <p className="mb-3">We do not sell your personal information. We may share your information only in the following circumstances:</p>
                  <ul className="mb-3">
                    <li><strong>Service Providers:</strong> We share information with trusted third-party service providers who perform services on our behalf:
                      <ul className="mt-2">
                        <li><strong>Auth0:</strong> For user authentication and identity management</li>
                        <li><strong>Amazon Web Services (AWS):</strong> For cloud hosting, database storage (DynamoDB), and serverless computing (Lambda, API Gateway)</li>
                        <li><strong>OpenAI:</strong> For AI-powered job description summarization (only job descriptions are sent, not your personal information)</li>
                        <li><strong>Email and Analytics Services:</strong> For communication and service analytics</li>
                      </ul>
                    </li>
                    <li><strong>AI Processing:</strong> When you use our AI features, job descriptions may be sent to OpenAI for processing. We do not send your personal information, profile data, or other sensitive data to OpenAI—only the job description text you provide.</li>
                    <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or in response to valid requests by public authorities (e.g., law enforcement).</li>
                    <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, subject to the same privacy protections.</li>
                    <li><strong>With Your Consent:</strong> We may share your information with your explicit consent for specific purposes.</li>
                    <li><strong>Protection of Rights:</strong> We may disclose information if we believe it is necessary to protect our rights, property, or safety, or that of our users or others.</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    5. Data Storage and Security
                  </h2>
                  <p className="mb-3">
                    <strong>Data Storage:</strong> Your information is stored securely in Amazon DynamoDB, a managed database service provided by AWS. Your data is stored in the United States unless otherwise specified. Job activity events are automatically deleted after 30 days as part of our data retention policy.
                  </p>
                  <p className="mb-3">
                    <strong>Security Measures:</strong> We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction, including:
                  </p>
                  <ul className="mb-3">
                    <li>Encryption in transit (HTTPS/TLS) for all data transmission</li>
                    <li>Encryption at rest for data stored in our databases</li>
                    <li>JWT token-based authentication via Auth0 for secure API access</li>
                    <li>Access controls and authentication requirements for all user data</li>
                    <li>Regular security reviews and updates</li>
                    <li>Secure serverless infrastructure on AWS</li>
                  </ul>
                  <p>
                    However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security. We encourage you to use strong passwords and keep your authentication credentials secure.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    6. Your Rights and Choices
                  </h2>
                  <p className="mb-3">Depending on your location, you may have the following rights regarding your personal information:</p>
                  <ul className="mb-3">
                    <li><strong>Access:</strong> Request access to your personal information, including your profile data, job postings, and account information</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate or incomplete information through your account settings or by contacting us</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal information and account. Note that some information may be retained as required by law or for legitimate business purposes</li>
                    <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
                    <li><strong>Data Management:</strong> Delete individual job postings, update your profile, or modify your account information at any time through the service</li>
                    <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time by using the unsubscribe link in emails or contacting us</li>
                    <li><strong>Objection:</strong> Object to certain processing activities, such as AI processing of your job descriptions</li>
                    <li><strong>Account Closure:</strong> Close your account at any time, which will initiate the deletion of your personal data subject to our retention policies</li>
                  </ul>
                  <p className="mb-3">
                    <strong>Data Retention:</strong> We retain your information for as long as your account is active or as needed to provide services to you. Job activity events are automatically deleted after 30 days. If you delete your account, we will delete or anonymize your personal information, except where we are required to retain it by law or for legitimate business purposes.
                  </p>
                  <p>
                    To exercise these rights, please contact us at the email address provided in the "Contact Us" section below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    7. Cookies and Tracking Technologies
                  </h2>
                  <p className="mb-3">
                    We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
                  </p>
                  <p className="mb-3">
                    You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
                  </p>
                  <p>
                    We use cookies for purposes such as authentication, preferences, analytics, and advertising.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    8. Children's Privacy
                  </h2>
                  <p>
                    Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    9. International Data Transfers
                  </h2>
                  <p>
                    Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our service, you consent to the transfer of your information to these facilities.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    10. Changes to This Privacy Policy
                  </h2>
                  <p>
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3" style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif"
                  }}>
                    11. Contact Us
                  </h2>
                  <p className="mb-3">
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <p className="mb-2">
                    <strong>Email:</strong> privacy@jobsreadyai.com
                  </p>
                  <p>
                    <strong>Website:</strong> <a href="/" className="text-primary">www.jobsreadyai.com</a>
                  </p>
                </section>
              </div>

              <div className="mt-5 pt-4 border-top">
                <Link to="/" className="btn btn-primary">
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
  )
}

export default PrivacyPolicy

